import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import NotificationDropdown from "./notifications/NotificationDropdown";
import BannerWizard from "./BannerWizard";
import { fetchAllBanners, fetchActiveBanners } from "../services/bannerService";
import "./Header.css";

export default function Header() {

  const { user: contextUser, logout } = useAuth();

  const [user, setUser] = useState(contextUser);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [channelLogo, setChannelLogo] = useState(null);

  // Ticker state
  const [allBanners, setAllBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fade, setFade] = useState(true);

  // Wizard state
  const [wizardBanners, setWizardBanners] = useState([]);
  const [showWizard, setShowWizard] = useState(false);

  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const mountedRef = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();

  /* =========================================================
     FETCH BANNERS FOR TICKER
  ========================================================= */

  useEffect(() => {
    const loadBanners = async () => {
      try {
        const data = await fetchAllBanners()
        if (data && data.length > 0) {
          setAllBanners(data)
        }
      } catch (e) {
        console.error("Failed to load banners", e)
      }
    }
    loadBanners()
  }, [])

  /* =========================================================
     ROTATING TICKER ANIMATION
  ========================================================= */

  useEffect(() => {
    if (allBanners.length <= 1) return

    mountedRef.current = true

    const runCycle = () => {
      if (!mountedRef.current) return
      setFade(false)
      timeoutRef.current = setTimeout(() => {
        if (!mountedRef.current) return
        setCurrentIndex((i) => (i + 1) % allBanners.length)
        setFade(true)
      }, 500)
    }

    intervalRef.current = setInterval(runCycle, 3000)

    return () => {
      mountedRef.current = false
      clearInterval(intervalRef.current)
      clearTimeout(timeoutRef.current)
    }
  }, [allBanners])

  /* =========================================================
     TICKER CLICK — open wizard for that banner
  ========================================================= */

  const handleTickerClick = async () => {
    const clicked = allBanners[currentIndex]
    if (!clicked) return

    try {
      const active = await fetchActiveBanners()
      const found = active?.find(a => a.banner?.id === clicked.id)
      // use active state if found (preserves currentPage/status), otherwise raw banner
      setWizardBanners([found ?? { banner: clicked, currentPage: 0, status: "pending" }])
    } catch {
      setWizardBanners([{ banner: clicked, currentPage: 0, status: "pending" }])
    }

    setShowWizard(true)
  }

  const handleWizardDone = () => {
    setShowWizard(false)
    setWizardBanners([])
  }

  /* =========================================================
     CHANNEL LOGO
  ========================================================= */

  useEffect(() => {
    const match = location.pathname.match(/^\/channel\/([^/]+)/)
    if (!match) { setChannelLogo(null); return }
    const key = "adm_panel_channelInfo_" + match[1]
    const stored = localStorage.getItem(key)
    if (!stored) { setChannelLogo(null); return }
    try {
      const parsed = JSON.parse(stored)
      setChannelLogo(parsed?.u || null)
    } catch {
      setChannelLogo(null)
    }
  }, [location.pathname])

  /* =========================================================
     USER SYNC
  ========================================================= */

  useEffect(() => {
    if (contextUser) { setUser(contextUser); return }
    const stored = localStorage.getItem("adm_panel_user")
    setUser(stored ? JSON.parse(stored) : null)
  }, [contextUser])

  /* =========================================================
     LOGOUT
  ========================================================= */

  const handleLogout = () => {
    logout()
    navigate("/login")
  }

  /* =========================================================
     HEADER HIDE
  ========================================================= */

  if (location.pathname === "/login" || location.pathname === "/") {
    return null
  }

  const canAccessUserManagement =
    user && (user.role === "ADMIN" || user.role === "SUPERADMIN")

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <>
      <header className="app-header">

        <div className="header-left">
          <Link to="/home" className="header-logo">
            <img
              src="/mongrel-logo.png"
              alt="Mongrel"
              className="logo-image"
            />
          </Link>
          {/* TICKER */}
          {allBanners.length > 0 && (
            <div
              className="header-ticker"
              onClick={handleTickerClick}
              title="Click to view"
            >
              <span className="header-ticker-label">New Update</span>
              <div className="header-ticker-container">
                <span className={`header-ticker-text ${fade ? "fade-in" : "fade-out"}`}>
                  {allBanners[currentIndex]?.title}
                </span>
              </div>
            </div>
          )}
        </div>


        <div className="header-right">

          {user && (
            <span className="user-name">
              Welcome, {user.username}
            </span>
          )}

          <NotificationDropdown />

          <div
            className="header-user-block"
            onClick={() => setDrawerOpen(true)}
          >
            {channelLogo ? (
              <img
                src={channelLogo}
                alt="Channel"
                className="header-channel-logo"
                onError={() => setChannelLogo(null)}
              />
            ) : (
              <div className="profile-btn">
                <i className="fa-solid fa-user"></i>
              </div>
            )}
          </div>

        </div>

      </header>

      {/* DRAWER OVERLAY */}
      <div
        className={`drawer-overlay ${drawerOpen ? "open" : ""}`}
        onClick={() => setDrawerOpen(false)}
      />

      {/* DRAWER */}
      <div className={`drawer ${drawerOpen ? "open" : ""}`}>

        <div className="drawer-header">
          <h3>User Menu</h3>
          <button className="drawer-close" onClick={() => setDrawerOpen(false)}>✕</button>
        </div>

        {channelLogo && (
          <div className="drawer-channel-logo">
            <img src={channelLogo} alt="Channel" />
          </div>
        )}

        <div className="drawer-body">

          <button className="drawer-btn" onClick={() => { setDrawerOpen(false); navigate("/home") }}>
            <i className="fa-solid fa-house"></i> Channels
          </button>

          <button className="drawer-btn" onClick={() => { setDrawerOpen(false); navigate("/profile") }}>
            <i className="fa-solid fa-user"></i> Profile
          </button>

          {canAccessUserManagement && (
            <button className="drawer-btn" onClick={() => { setDrawerOpen(false); navigate("/usermanagement") }}>
              <i className="fa-solid fa-users"></i> User Management
            </button>
          )}

          <button className="drawer-btn logout" onClick={handleLogout}>
            <i className="fa-solid fa-power-off"></i> Logout
          </button>

        </div>

      </div>

      {/* BANNER WIZARD */}
      {showWizard && wizardBanners.length > 0 && (
        <BannerWizard
          banners={wizardBanners}
          onDone={handleWizardDone}
        />
      )}
    </>
  )
}